import React, { useState, useRef, useEffect } from 'react';
import './Breadcrumb.scss';
import './HeaderElements.scss';
import { useAppDispatch, useAppSelector } from '../../../hooks/storeHooks';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import Loader from '../../common/Loader/Loader';
import SidePanel from '../../../components/common/side-panel/SidePanel';
import { Asset } from '../../common/Dropdown/Dropdown';
import { updateSelectedAsset } from '../AssetGroupsSlice';
import { fetchNotificationsAsync } from '../NotificationsSlice';
import { selectEventCount, resetEventCount } from '../EventCounterSlice';
import { setIndustryApplicationId } from '../../asset-analysis/AssetAnalysisSlice';
import { fetchDataHistoryTrendLibraryAsync } from './../../data-history/TrendLibrary/TrendLibrarySlice';
import GlobalWellHistorySearch from './GlobalWellHistorySearch';
import { ReactComponent as SliderIcon } from '../../../images/slider.svg';
import { ReactComponent as ListIcon } from '../../../images/list.svg';
import { ReactComponent as NotificationIcon } from '../../../images/notification-bell.svg';
import { ReactComponent as FilterIcon } from '../../../images/filter-funnel-icon.svg';

import Tooltip from '../../common/tooltip/ToolTip';
import NotificationPanel from '../../notifications/components/NotificationPanel';
import ControlActions, {
  WellControlAction,
  ItemType,
  ActionsToExclude,
} from '../../common/ControlActions/ControlActions';
import { UrlParams } from '../../asset/components/Asset';
import WellFilterPanel from '../../well-filters/WellFilterPanel';
import { showTooltip, hideTooltip } from '../../../components/common/simple-tool-tip/TooltipManager';
import { ReactComponent as ArrowUp } from '../../../images/arrow-narrow-up.svg';
import { ReactComponent as ArrowDown } from '../../../images/arrow-narrow-down.svg';
import ChevronDown from '../../asset-status/icons/ChevronDown';
import ChevronUp from '../../asset-status/icons/ChevronUp';

interface HeaderElementsProps {
  showWellSpecificHeader?: boolean;
  showWellsTab?: boolean;
  showFilterIcon?: boolean;
}

const HeaderElements: React.FC<HeaderElementsProps> = ({ showWellSpecificHeader, showWellsTab, showFilterIcon }) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { assetId } = useParams<keyof UrlParams>() as UrlParams;

  const [isWellGroupOpen, setIsWellGroupOpen] = React.useState(false);
  const [isActive, setIsActive] = useState(false);
  const [showAllWells] = useState(true);
  const [isWellFilterOpen, setIsWellFilterOpen] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const notificationRef = useRef<HTMLDivElement | null>(null);
  const notificationPromiseRef = useRef<any>(null);
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  const prevWellRef = useRef<HTMLSpanElement | null>(null);
  const nextWellRef = useRef<HTMLSpanElement | null>(null);

  // Handle clicking outside to close notification panel
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (notificationRef.current && !notificationRef.current.contains(event.target as Node)) {
        setShowNotifications(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showNotifications]);

  const selectedGroup = useAppSelector((state) => state.assetGroups?.selectedGroup);
  const selectedAssetId = useAppSelector((state) => state.assetGroups?.selectedAssetId);
  const wellControlActionTypes = useAppSelector((state) => state.assetData.wellControlActions);
  const isWellEnabled = useAppSelector((state) => state.assetData.wellEnabledStatus) ?? false;
  const canConfigWell = useAppSelector((state) => state.assetData.canConfigWell) ?? false;
  const eventCount = useAppSelector(selectEventCount);

  // Debug: Log the event count when it changes
  useEffect(() => {
    console.log('Event count changed:', eventCount);
  }, [eventCount]);

  const currentAssetURLPath = location.pathname.split('/')[2];
  const groupName = selectedGroup?.assetGroupName;
  const selectedGroupAssets = selectedGroup?.assets || [];
  const currentIndex = selectedGroupAssets.findIndex((a) => a.assetName === selectedAssetId);
  const actionsToExclude: ActionsToExclude[] = [
    {
      itemType: ItemType.WellControlAction,
      controlActionType: WellControlAction.ClearAlarms,
    },
    {
      itemType: ItemType.WellControlAction,
      controlActionType: WellControlAction.EnablePid,
    },
  ];

  const handleAssetIdChange = (asset: Asset) => {
    const assetId = asset.name;
    dispatch(fetchDataHistoryTrendLibraryAsync({ assetId: asset.id, groupName: groupName ?? '' }));
    dispatch(
      updateSelectedAsset({
        groupId: selectedGroup?.id,
        assetId: assetId,
        searchString: undefined,
        assetIdGuid: asset.id,
      }),
    );
    if (selectedGroup && selectedGroup.assetGroupName) {
      const formattedGroupName = selectedGroup.assetGroupName.split(' ').join('_');
      navigate(
        `/asset/${currentAssetURLPath}/${
          selectedGroup.assets.find((asset) => asset.assetName === assetId)?.assetId
        }/${formattedGroupName}`,
      );
    }

    const selectedAsset = selectedGroup?.assets.filter((asset) => asset.assetName === assetId)[0];

    // Cancel previous notification request if it exists
    if (notificationPromiseRef.current) {
      notificationPromiseRef.current.abort();
    }

    // Create new request and store the promise for cancellation
    notificationPromiseRef.current = dispatch(fetchNotificationsAsync({ type: 'asset', id: selectedAsset?.assetId ?? '' }));

    notificationPromiseRef.current.catch((error: any) => {
      if (error.name !== 'AbortError') {
        console.error('Asset notification request failed:', error);
      }
    });

    const industryApplicationId = selectedGroup?.assets.find((a) => a.assetName == assetId)?.industryApplicationId;
    dispatch(setIndustryApplicationId({ industryApplicationId: industryApplicationId }));
  };

  const goToPrevWell = () => {
    if (selectedGroupAssets.length === 0 || currentIndex <= 0) return;
    const prevAsset = selectedGroupAssets[currentIndex - 1];
    handleAssetIdChange({ name: prevAsset.assetName, id: prevAsset.assetId });
  };

  const goToNextWell = () => {
    if (selectedGroupAssets.length === 0 || currentIndex === -1 || currentIndex >= selectedGroupAssets.length - 1)
      return;
    const nextAsset = selectedGroupAssets[currentIndex + 1];
    handleAssetIdChange({ name: nextAsset.assetName, id: nextAsset.assetId });
  };

  const handlePrevWellMouseEnter = () => {
    if (currentIndex <= 0 && prevWellRef.current) {
      showTooltip('First page reached. No earlier pages to display', prevWellRef.current);
    }
  };

  const handlePrevWellMouseLeave = () => {
    if (currentIndex <= 0) {
      hideTooltip();
    }
  };

  const handleNextWellMouseEnter = () => {
    if ((currentIndex === -1 || currentIndex >= selectedGroupAssets.length - 1) && nextWellRef.current) {
      showTooltip('Last page reached. No further pages to display', nextWellRef.current);
    }
  };

  const handleNextWellMouseLeave = () => {
    if (currentIndex === -1 || currentIndex >= selectedGroupAssets.length - 1) {
      hideTooltip();
    }
  };
  return (
    <div className='HeaderElements'>
      <div
        className={`top-header-elements-container${showWellSpecificHeader ? ' well-specific-header' : ''}`}
        style={{ marginRight: '10px' }}
      >
        <GlobalWellHistorySearch
          className={`navigation-content-search header-elements-search ${isSearchExpanded ? 'expanded' : ''}`}
          onIconClick={() => setIsSearchExpanded((prev) => !prev)}
          onCollapse={() => setIsSearchExpanded(false)}
        />
        {showWellsTab && (
          <span>
            <div className='header-wells-toggle-container'>
              <div className='header-well-buttons'>
                <button
                  className='header-wells-toggle-button'
                  type='button'
                  // onClick={() => setShowAllWells(false)}
                  disabled
                  style={{ opacity: 0.5, cursor: 'not-allowed' }}
                >
                  My wells
                </button>
                <button
                  className={`header-wells-toggle-button${showAllWells ? ' active' : ''}`}
                  // onClick={() => setShowAllWells(true)}
                  type='button'
                >
                  All wells
                </button>
              </div>
            </div>
          </span>
        )}
        {showFilterIcon && (
          <span>
            <Tooltip content='Filter' direction='left'>
              <div
                className={`top-header-elements-icons ${isWellFilterOpen ? 'selected' : ''}`}
                onClick={() => setIsWellFilterOpen(true)}
              >
                <FilterIcon />
              </div>
            </Tooltip>
            <WellFilterPanel isWellFilterOpen={isWellFilterOpen} setIsWellFilterOpen={setIsWellFilterOpen} />
          </span>
        )}
        <span style={{ marginRight: '10px', position: 'relative' }} ref={notificationRef}>
          <Tooltip content='Notifications' direction='left'>
            <div
              onClick={(e) => {
                e.stopPropagation();
                setShowNotifications((prev) => {
                  const newShowState = !prev;
                  // Reset event count when opening notifications panel
                  if (newShowState && eventCount > 0) {
                    dispatch(resetEventCount());
                  }
                  return newShowState;
                });
              }}
              className={`top-header-elements-icons-notification ${showNotifications ? 'selected' : ''}`}
            >
              <NotificationIcon />
              {eventCount > 0 && (
                <div className='notification-badge'>
                  <span className='notification-badge-text'>{eventCount > 99 ? '99+' : eventCount}</span>
                </div>
              )}
            </div>
          </Tooltip>

          {showNotifications && (
            <div style={{ position: 'absolute', top: '47px', right: '0px', zIndex: 1000 }}>
              <NotificationPanel />
            </div>
          )}
        </span>
      </div>
      {showWellSpecificHeader && (
        <div className='bottom-header-elements-container'>
          <span
            ref={prevWellRef}
            className={`chevron-btn${currentIndex > 0 ? '' : ' nomorewells'}`}
            onClick={goToPrevWell}
            onMouseEnter={handlePrevWellMouseEnter}
            onMouseLeave={handlePrevWellMouseLeave}
          >
            <Tooltip content='Previous well' direction='left'>
              <ArrowUp color={currentIndex > 0 ? '#CDD5DF' : '#5D6675'} />
            </Tooltip>
          </span>
          <span
            ref={nextWellRef}
            className={`chevron-btn${currentIndex < selectedGroupAssets.length - 1 && currentIndex !== -1 ? '' : ' nomorewells'}`}
            onClick={goToNextWell}
            onMouseEnter={handleNextWellMouseEnter}
            onMouseLeave={handleNextWellMouseLeave}
          >
            <Tooltip content='Next well' direction='top'>
              <ArrowDown
                color={currentIndex < selectedGroupAssets.length - 1 && currentIndex !== -1 ? '#CDD5DF' : '#5D6675'}
              />
            </Tooltip>
          </span>
          <span onClick={() => setIsWellGroupOpen(true)} className='well-list-btn'>
            <ListIcon /> Well list
          </span>
          <div className='card-viewer-settings' data-testid='card-viewer-settings' style={{ marginTop: '8px' }}>
            <ControlActions
              assetId={assetId}
              label='Well control'
              isWellEnabled={isWellEnabled}
              canConfigWell={canConfigWell}
              supportedControlActionIds={wellControlActionTypes.map((x) => x.id)}
              buttonContent={
                <span
                  data-testid='control-actions-button'
                  onClick={() => {
                    setIsActive(!isActive);
                  }}
                >
                  <SliderIcon /> Well control{' '}
                  {isActive ? <ChevronUp color='#FFFFFF' /> : <ChevronDown color='#FFFFFF' />}
                </span>
              }
              actionsToExclude={actionsToExclude}
              modeOptionViewPosition='right'
              onMenuClose={() => setIsActive(false)}
            />
          </div>
        </div>
      )}
      <SidePanel open={isWellGroupOpen} onClose={() => setIsWellGroupOpen(false)} header='Wells' width={'416px'}>
        <ul className='side-panel-well-list header-elements-list'>
          {selectedGroup?.assets?.length === 0 ? (
            <Loader />
          ) : (
            selectedGroup?.assets.map((asset) => (
              <li
                key={asset.assetId}
                className={
                  'side-panel-well-group-items header-elements-well-names' +
                  (asset.assetName === selectedAssetId ? ' selected' : '')
                }
                onClick={() => handleAssetIdChange({ name: asset.assetName, id: asset.assetId })}
              >
                <span className='side-panel-well-group-name'>{asset.assetName}</span>
              </li>
            ))
          )}
        </ul>
      </SidePanel>
    </div>
  );
};

export default HeaderElements;
